import { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faTimes } from '@/fontawesome';

export interface MultiSelectOption<T> {
  value: T;
  label: string;
}

interface MultiSelectDropdownProps<T> {
  options: MultiSelectOption<T>[];
  selected: T[];
  onChange: (selected: T[]) => void;
  label?: string;
  icon?: React.ReactNode;
  placeholder?: string;
  selectedCountSuffix?: string;
  className?: string;
  dropdownClassName?: string;
}

export default function MultiSelectDropdown<T extends string>({
  options,
  selected,
  onChange,
  label,
  icon,
  placeholder = 'Alle',
  selectedCountSuffix = 'ausgewählt',
  className = '',
  dropdownClassName = 'multi-select-dropdown',
}: MultiSelectDropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(`.${dropdownClassName}`)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownClassName]);

  const handleToggle = (value: T) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const handleSelectAll = () => {
    onChange(options.map(o => o.value));
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const getDisplayText = (): string => {
    if (selected.length === 0) {
      return placeholder;
    }
    if (selected.length === 1) {
      const option = options.find(o => o.value === selected[0]);
      return option?.label ?? String(selected[0]);
    }
    return `${selected.length} ${selectedCountSuffix}`;
  };

  return (
    <div className={`relative ${dropdownClassName} ${className}`} ref={containerRef}>
      {label !== undefined && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {icon}
          {label}
        </label>
      )}
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
        >
          <span>{getDisplayText()}</span>
          <div className="flex items-center space-x-1">
            {selected.length > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearAll();
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer"
              >
                <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
              </button>
            )}
            <FontAwesomeIcon
              icon={faChevronRight}
              className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-90' : ''}`}
            />
          </div>
        </button>
        {isOpen && (
          <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg">
            <div className="p-2 border-b border-gray-200 dark:border-gray-600">
              <div className="flex space-x-2">
                <button
                  onClick={handleSelectAll}
                  className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-900/70 cursor-pointer"
                >
                  Alle
                </button>
                <button
                  onClick={handleClearAll}
                  className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-900/50 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-900/70 cursor-pointer"
                >
                  Keine
                </button>
              </div>
            </div>
            <div className="p-2 max-h-48 overflow-y-auto">
              {options.map(option => (
                <label
                  key={option.value}
                  className="flex items-center space-x-2 text-sm py-1 px-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(option.value)}
                    onChange={() => handleToggle(option.value)}
                    className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-700 dark:text-gray-300">{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
